import { Component, Input, NgZone, OnDestroy } from "@angular/core";
import moment from 'moment';
import { Subscription } from "rxjs";
import { ImpersonationService, ProfileType } from "../../../../auth/apigee/impersonation.service";
import { LoggerService } from "../../../../common/logging/logger.service";
import { ModalDialogComponent } from "../../../../common/modal/modalDialog.component";
import { OfficeSelectorService } from "../../../../common/officeSelector/officeSelector.service";
import { VisitsCallsTargetsService, VisitsCallsTargetsServiceData, OfferingDetailData } from "../../services/visitsCallsTargetsKpi/visitsCallsTargets.service";
import { ColumnDefinition, DateColumnDefinition, TextColumnDefinition, NumberColumnDefinition } from "../../../../dashboardCommon/components/cardDetails/datatable.component";
import { SingleSummaryDetailKpiServiceHost } from "../../../../dashboardCommon/components/kpiServiceHostComponent";

export class OfferingsDOData {
    public consultant: number;
    public office: number;
    public target: number;

    constructor(consultant: number, office: number) {
        this.consultant = consultant;
        this.office = office;
        this.target = 20;
    }
}

export class OfferingsDODetailData {
    public columnDefinitions: ColumnDefinition[];
    public tabledata: any[][];

    constructor(columnDefinitions: ColumnDefinition[], tabledata: any[][]) {
        this.columnDefinitions = columnDefinitions;
        this.tabledata = tabledata;
    }
}

class OfficeOfferingEntry {
    public consultant: string;
    public offering: OfferingDetailData;

    constructor(consultant: string, offering: OfferingDetailData) {
        this.consultant = consultant;
        this.offering = offering;
    }
}

@Component({
    selector: 'offerings-do-kpi-service-host',
    template: '<ng-content></ng-content>',
    standalone: false
})
export class OfferingsDOKpiServiceHostComponent extends SingleSummaryDetailKpiServiceHost<VisitsCallsTargetsService, VisitsCallsTargetsServiceData, OfferingsDOData, OfferingsDODetailData>
    implements OnDestroy {
    public getClassName() { return "OfferingsDOKpiServiceHostComponent"; }

    private raasSubscription: Subscription;
    private officeSelectorSubscription: Subscription;
    @Input() public detailDialog: ModalDialogComponent;
    @Input() public messageDialog: ModalDialogComponent;

    constructor(zone: NgZone, logger: LoggerService, private impersonationService: ImpersonationService, private officeSelectorService: OfficeSelectorService,
        summaryService: VisitsCallsTargetsService) {
        super(zone, logger, summaryService);
        this.raasSubscription = this.impersonationService.impersonationChanged.subscribe(() => { this.resetSummary(); });
        this.officeSelectorSubscription = this.officeSelectorService.selectedOfficeUpdated.subscribe(() => { this.resetSummary(); });
    }

    protected processSummaryData(data: VisitsCallsTargetsServiceData, updateRequested: Boolean): OfferingsDOData {

        if (data.consultantEntry) {
            let thisWeek = moment().isoWeek();
            let updateTime = this.extractUpdateTime(data);
            let now = moment();
            if (updateTime && moment(updateTime).isoWeek() == thisWeek) {
                let diff = moment.duration(now.diff(moment(data.consultantEntry.visitsUpdateTimestamp)));
                if (diff.hours() < 2 && this.summaryData && updateRequested) {
                    this.messageDialog.open();
                }

                let consultant = (data.consultantEntry.offerings || []).filter(x => moment(x.updateTimestamp).isoWeek() == thisWeek).length;
                let office = (data.entries || []).map(x => x.offerings).reduce((a, b) => (a || <OfferingDetailData[]>[]).concat(b || <OfferingDetailData[]>[]))
                    .filter(x => moment(x.updateTimestamp).isoWeek() == thisWeek).length;
                return new OfferingsDOData(consultant, office);
            } else {
                return this.getDefaultSummaryData();
            }
        } else {
            let thisWeek = moment().isoWeek();
            let office = (data.entries || []).map(x => x.offerings).reduce((a, b) => (a || <OfferingDetailData[]>[]).concat(b || <OfferingDetailData[]>[]))
                .filter(x => moment(x.updateTimestamp).isoWeek() == thisWeek).length;
            return new OfferingsDOData(0, office);
        }
    }

    protected getDefaultSummaryData(): OfferingsDOData {
        let data = new OfferingsDOData(0, 0);
        return data;
    }

    protected extractUpdateTime(data: VisitsCallsTargetsServiceData): Date {
        if (data.consultantEntry) {
            return data.consultantEntry.offeringsUpdateTimestamp;
        } else {
            return new Date();
        }
    }

    protected processDetailData(data: VisitsCallsTargetsServiceData, args: any): OfferingsDODetailData {
        let thisWeek = moment().isoWeek();

        let columnDefinitions = [
            new TextColumnDefinition("Cliente", null, true),
            new TextColumnDefinition("Segmento", null, true),
            new DateColumnDefinition("Fecha", (data) => moment(data).format("DD/MM/YYYY")),
            new NumberColumnDefinition("Nº ofrecimientos"),
            new NumberColumnDefinition("Nº candidatos"),
            new TextColumnDefinition("Puesto", null, true),
        ];

        let tabledata = [];

        columnDefinitions.push(new TextColumnDefinition("Consultor", null, true));

        let offerings = (data.entries || [])
            .map(x => (x.offerings || [])
                .map(y => new OfficeOfferingEntry(x.name, y)))
            .reduce((x, y) => x.concat(y))
            .filter(x => moment(x.offering.updateTimestamp).isoWeek() == thisWeek)
            .sort(function (a, b) {
                if (a.offering.date > b.offering.date) {
                    return 1;
                }
                if (a.offering.date < b.offering.date) {
                    return -1;
                }
                // a must be equal to b
                return 0;
            });

        for (let item of offerings) {
            tabledata.push(
                [
                    item.offering.clientName,
                    "S" + item.offering.segment,
                    moment(item.offering.date).startOf("day"),
                    item.offering.offeringCount,
                    item.offering.candidateCount,
                    item.offering.jobTitle,
                    item.consultant
                ]);
        }

        return new OfferingsDODetailData(columnDefinitions, tabledata);
    }

    protected getDefaultDetailData() {
        let columnDefinitions = [
            new TextColumnDefinition("Cliente", null, true),
            new TextColumnDefinition("Segmento", null, true),
            new DateColumnDefinition("Fecha", (data) => moment(data).format("DD/MM/YYYY")),
            new NumberColumnDefinition("Nº ofrecimientos"),
            new NumberColumnDefinition("Nº candidatos"),
            new TextColumnDefinition("Puesto", null, true),
        ];

        return new OfferingsDODetailData(columnDefinitions, []);
    }

    protected performDetailAction(): void {
        if (this.detailDialog) this.detailDialog.open();
    }

    public mergeFilter(filter: any) {
        let detailProcessingArgs = this.detailProcessingArgs || {};
        detailProcessingArgs.filter = detailProcessingArgs.filter || {};
        detailProcessingArgs.filter = { ...detailProcessingArgs.filter, ...filter };
        this.detailProcessingArgs = detailProcessingArgs;
    }

    public ngOnDestroy(): void {
        if (this.raasSubscription) this.raasSubscription.unsubscribe();
        if (this.officeSelectorSubscription) this.officeSelectorSubscription.unsubscribe();
    }
}
